import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import languageConfig from '../config/languages';

/**
 * Hook to handle language switching via URL parameters
 * Supports ?lang=hr or ?lng=hr parameters
 *
 * Example URLs:
 * - https://ulpian.com/?lang=hr#video
 * - https://ulpian.com/?lang=en#features
 * - https://ulpian.com/coverage?lang=hr
 */
export function useLanguageFromURL() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const handleLanguageChange = async () => {
      const params = new URLSearchParams(location.search);
      const langParam = params.get('lang') || params.get('lng');

      if (langParam && languageConfig.isLanguageEnabled(langParam)) {
        // Only change if different from current language
        if (i18n.language !== langParam) {
          // Clear any existing language preference to ensure URL param takes priority
          localStorage.removeItem('i18nextLng');

          // Force language change and wait for it to complete
          await i18n.changeLanguage(langParam);

          // Store the new language in localStorage
          localStorage.setItem('i18nextLng', langParam);

          // Force a re-render by updating the document language attribute
          document.documentElement.lang = langParam;
        }
      }
    };

    // Handle immediately
    handleLanguageChange();

    // Also handle after a small delay for initialization
    const timer = setTimeout(handleLanguageChange, 100);

    return () => clearTimeout(timer);
  }, [location.search, i18n]);
}
